import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    DialogContent,
    DialogTitle,
    Grid2,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { ArticleDto } from '../../api/ClientApi';
import ArticleAutocomplete from '../../common/components/autocomplete/ArticleAutocomplete';
import {
    calculateTemplateCost,
    ProductTemplateModel,
    TemplateCostLine,
    TemplateCostResponse,
} from '../../api/Extensions';

import { FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

type ProductTemplateCostViewProps = {
    open: boolean;
    template?: ProductTemplateModel;
    articles: ArticleDto[];
    onClose(): void;
};

interface WorkItem {
    key: number;
    articleId?: number;
    quantity: number;
    priceOverride?: number;
}

function formatMoney(value?: number): string {
    if (value === undefined || value === null) {
        return '—';
    }
    return value.toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ProductTemplateCostView(props: ProductTemplateCostViewProps): ReactElement {
    const { open, template, articles, onClose } = props;

    const [items, setItems] = useState<WorkItem[]>([]);
    const [asOf, setAsOf] = useState<Dayjs>(dayjs());
    const [cost, setCost] = useState<TemplateCostResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [nextKey, setNextKey] = useState(1);

    // Seed the working list from the template every time it is (re)opened. Edits from here never
    // touch the saved template — this is a scratch list, like a copied Excel sheet.
    useEffect(() => {
        if (open && template) {
            const seeded = (template.items ?? []).map((it, index) => ({
                key: index + 1,
                articleId: it.articleId,
                quantity: it.quantity,
                priceOverride: undefined,
            }));
            setItems(seeded);
            setNextKey(seeded.length + 1);
            setAsOf(dayjs());
            setCost(null);
        }
    }, [open, template]);

    // Recompute the price whenever the list or the target date changes (debounced).
    useEffect(() => {
        if (!open) {
            return;
        }

        const priced = items.filter((i) => !!i.articleId && i.articleId > 0);

        if (priced.length === 0) {
            setCost(null);
            return;
        }

        const handle = setTimeout(() => {
            setLoading(true);
            calculateTemplateCost({
                asOf: asOf ? asOf.toDate().toISOString() : undefined,
                items: priced.map((i) => ({
                    articleId: i.articleId as number,
                    quantity: i.quantity,
                    priceOverride: i.priceOverride,
                })),
            })
                .then((res) => setCost(res))
                .catch(() => setCost(null))
                .finally(() => setLoading(false));
        }, 400);

        return () => clearTimeout(handle);
    }, [open, items, asOf]);

    // Server returns lines in the same order as the priced items we sent; map them back by that order.
    const lineByKey = useMemo(() => {
        const map = new Map<number, TemplateCostLine>();
        if (cost) {
            const priced = items.filter((i) => !!i.articleId && i.articleId > 0);
            priced.forEach((i, index) => {
                if (cost.lines[index]) {
                    map.set(i.key, cost.lines[index]);
                }
            });
        }
        return map;
    }, [cost, items]);

    const updateItem = (key: number, patch: Partial<WorkItem>) => {
        setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));
    };

    const addRow = () => {
        setItems((prev) => [...prev, { key: nextKey, quantity: 1, articleId: undefined, priceOverride: undefined }]);
        setNextKey((k) => k + 1);
    };

    const removeRow = (key: number) => {
        setItems((prev) => prev.filter((i) => i.key !== key));
    };

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <FormDialogTitle subtitle={template?.description} onClose={onClose}>
                Cena sastavnice: {template?.title}
            </FormDialogTitle>
            <DialogContent dividers>
                <Grid2 container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid2 size={{ xs: 12, sm: 5, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label="Cena na dan"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={asOf}
                                onChange={(value) => value && setAsOf(value)}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 7, md: 8 }} sx={{ textAlign: { sm: 'right' } }}>
                        <Typography variant="h5" component="span">
                            Ukupno: <strong>{formatMoney(cost?.total)}</strong>
                        </Typography>
                        {loading && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                (računam…)
                            </Typography>
                        )}
                    </Grid2>
                </Grid2>

                {cost && !cost.allPricesKnown && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Za neke artikle nema poznate cene — ukupan zbir ih ne uključuje. Unesi ručnu cenu da bi ušli u
                        zbir.
                    </Alert>
                )}

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '40%' }}>Artikal</TableCell>
                            <TableCell align="right" sx={{ width: 110 }}>
                                Količina
                            </TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>
                                Poslednja cena
                            </TableCell>
                            <TableCell align="right" sx={{ width: 140 }}>
                                Ručna cena
                            </TableCell>
                            <TableCell align="right" sx={{ width: 140 }}>
                                Iznos
                            </TableCell>
                            <TableCell align="right" sx={{ width: 50 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const line = lineByKey.get(item.key);
                            const unknown = !!item.articleId && !!line && !line.priceKnown;

                            return (
                                <TableRow key={item.key}>
                                    <TableCell>
                                        <ArticleAutocomplete
                                            isRequired
                                            articles={articles}
                                            selectedArticleId={item.articleId}
                                            selectArticle={(articleId) => updateItem(item.key, { articleId })}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                            size="small"
                                            value={item.quantity}
                                            onChange={(e) =>
                                                updateItem(item.key, { quantity: Number(e.target.value) || 0 })
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {unknown ? (
                                            <Chip size="small" color="warning" label="nepoznata" />
                                        ) : (
                                            <>
                                                {formatMoney(line?.unitPrice)}
                                                {line?.unitPriceDate && !line.priceOverridden && (
                                                    <Tooltip
                                                        title={
                                                            'Iz nabavke: ' +
                                                            new Date(line.unitPriceDate).toLocaleString('sr-RS')
                                                        }
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {new Date(line.unitPriceDate).toLocaleDateString('sr-RS')}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                            size="small"
                                            placeholder="—"
                                            value={item.priceOverride ?? ''}
                                            onChange={(e) =>
                                                updateItem(item.key, {
                                                    priceOverride:
                                                        e.target.value === '' ? undefined : Number(e.target.value),
                                                })
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{formatMoney(line?.lineTotal)}</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => removeRow(item.key)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Nema stavki. Dodaj artikal da bi video cenu.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Button startIcon={<AddIcon />} onClick={addRow}>
                        Dodaj artikal
                    </Button>
                    <Button variant="contained" onClick={onClose}>
                        Zatvori
                    </Button>
                </Box>
            </DialogContent>
        </ResponsiveDialog>
    );
}
